(() => {
  if (customElements.get('mv-cinematic-hero')) return;
  customElements.define('mv-cinematic-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (el, name, fn, options = {}) => el?.addEventListener(name, fn, { ...options, signal: this.controller.signal });
      const slides = [...this.querySelectorAll('[data-slide]')];
      const thumbs = [...this.querySelectorAll('[data-go]')];
      if (!slides.length) return;
      const stage = this.querySelector('.mv-cinema__stage');
      const rail = this.querySelector('.mv-cinema__thumbs');
      const toggle = this.querySelector('[data-toggle]');
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      const speed = Math.max(4, Number(this.dataset.speed) || 7) * 1000;
      const holds = new Set();
      let pointerFrame = 0, entranceTimer = 0;
      let active = 0, userPaused = this.dataset.autoplay !== 'true' || reduce.matches, visible = true, timer = 0, touch = null;
      this.style.setProperty('--duration', `${speed}ms`);
      const run = () => {
        clearTimeout(timer);
        thumbs.forEach(t => t.classList.remove('is-running'));
        this.toggleAttribute('data-paused', userPaused);
        this.toggleAttribute('data-suspended', !visible || document.hidden);
        toggle?.setAttribute('aria-label', userPaused ? toggle.dataset.play : toggle.dataset.pause);
        if (userPaused || !visible || document.hidden || holds.size || slides.length < 2) return;
        void thumbs[active]?.offsetWidth;
        thumbs[active]?.classList.add('is-running');
        timer = setTimeout(() => go(active + 1), speed);
      };
      const go = (index, manual = false) => {
        const previous = active;
        active = (index + slides.length) % slides.length;
        if (previous !== active) {
          clearTimeout(entranceTimer);
          slides.forEach(slide => slide.classList.remove('is-entering'));
        }
        slides.forEach((slide, i) => {
          const current = i === active;
          slide.classList.toggle('is-active', current);
          slide.classList.toggle('is-entering', current && !reduce.matches && previous !== active);
          slide.inert = !current;
          slide.setAttribute('aria-hidden', String(!current));
        });
        thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === active)));
        entranceTimer = setTimeout(() => slides.forEach(slide => slide.classList.remove('is-entering')), 1600);
        this.style.setProperty('--accent', slides[active].dataset.accent);
        this.querySelector('[data-current]').textContent = String(active + 1).padStart(2, '0');
        const thumb = thumbs[active];
        if (thumb && rail) {
          const left = thumb.offsetLeft - rail.offsetLeft;
          if (left < rail.scrollLeft || left + thumb.offsetWidth > rail.scrollLeft + rail.clientWidth) {
            rail.scrollTo({ left: Math.max(0, left - (rail.clientWidth - thumb.offsetWidth) / 2), behavior: reduce.matches ? 'instant' : 'smooth' });
          }
        }
        if (manual) this.querySelector('[data-announcement]').textContent = slides[active].getAttribute('aria-label');
        run();
      };
      thumbs.forEach(thumb => on(thumb, 'click', () => go(Number(thumb.dataset.go), true)));
      this.querySelectorAll('[data-step]').forEach(btn => on(btn, 'click', () => go(active + Number(btn.dataset.step), true)));
      on(toggle, 'click', () => { userPaused = !userPaused; holds.delete('focus'); run(); });
      on(this, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || event.target.matches('input,textarea,select')) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : active + (event.key === 'ArrowRight' ? 1 : -1);
        userPaused = true; go(next, true); thumbs[active].focus({ preventScroll: true });
      });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { holds.add('hover'); run(); } });
      on(this, 'pointerleave', () => { holds.delete('hover'); run(); slides.forEach(s => { s.style.removeProperty('--pointer-x'); s.style.removeProperty('--pointer-y'); s.style.removeProperty('--tilt-x'); s.style.removeProperty('--tilt-y'); }); });
      on(this, 'focusin', event => { if (event.target !== toggle) { holds.add('focus'); run(); } });
      on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { holds.delete('focus'); run(); } });
      on(stage, 'pointerdown', event => {
        if (event.pointerType === 'touch') { touch = { x: event.clientX, y: event.clientY }; holds.add('touch'); run(); }
      }, { passive: true });
      on(window, 'pointerup', event => {
        if (!touch) return;
        const dx = event.clientX - touch.x, dy = event.clientY - touch.y;
        touch = null; holds.delete('touch');
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.25) { userPaused = true; go(active + (dx < 0 ? 1 : -1), true); }
        else run();
      }, { passive: true });
      on(window, 'pointercancel', () => { touch = null; holds.delete('touch'); run(); });
      on(stage, 'pointermove', event => {
        if (event.pointerType !== 'mouse' || reduce.matches || userPaused || this.dataset.motion !== 'true') return;
        if (pointerFrame) return;
        pointerFrame = requestAnimationFrame(() => {
          pointerFrame = 0;
          const r = stage.getBoundingClientRect();
          const x = (event.clientX - r.left) / r.width - .5;
          const y = (event.clientY - r.top) / r.height - .5;
          slides[active].style.setProperty('--pointer-x', `${x * 26}px`);
          slides[active].style.setProperty('--pointer-y', `${y * 18}px`);
          slides[active].style.setProperty('--tilt-x', `${-y * 5}deg`);
          slides[active].style.setProperty('--tilt-y', `${x * 7}deg`);
        });
      }, { passive: true });
      on(document, 'visibilitychange', run);
      on(reduce, 'change', () => { if (reduce.matches) userPaused = true; run(); });
      on(document, 'shopify:block:select', event => {
        const i = slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (i >= 0) { userPaused = true; go(i, true); }
      });
      this.observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; run(); }, { threshold: .15 });
      this.observer.observe(stage);
      this.stop = () => { clearTimeout(timer); clearTimeout(entranceTimer); cancelAnimationFrame(pointerFrame); };
      if (slides.length < 2) this.querySelector('.mv-cinema__navigation').hidden = true;
      run();
    }
    disconnectedCallback() { this.stop?.(); this.controller?.abort(); this.observer?.disconnect(); this.controller = null; }
  });
})();
